import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Persist the user's last-picked sort so re-mounts / page reloads remember it.
const SORT_KEY = "stratos.heatmap.sort";
const DENSITY_KEY = "stratos.heatmap.density";
const SORT_OPTIONS = [
  { value: "bull_desc", label: "Bull %" },
  { value: "alpha", label: "A-Z" },
  { value: "updated", label: "Updated" },
];

/**
 * Compact SVG sparkline for a bullish %-series (0-100). Fixed size so
 * heatmap cells stay uniform. Line colour matches the delta direction so
 * you can read the trend without pixel-peeping.
 */
function MoodSpark({ data, delta, testId }) {
  const W = 34;
  const H = 12;
  const pad = 1;
  if (!data || data.length < 2) return null;
  const stroke = delta > 0 ? "#00C805" : delta < 0 ? "#FF3B30" : "rgba(255,255,255,0.5)";
  const min = 0;
  const max = 100;
  const stepX = (W - pad * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const clamped = Math.max(min, Math.min(max, v));
    const x = pad + i * stepX;
    const y = H - pad - ((clamped - min) / (max - min)) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg
      data-testid={testId}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="shrink-0"
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Compact heatmap of every watchlist symbol colour-coded by its latest
 * bullish %. Green = bullish-heavy, red = bearish-heavy, zinc = no data.
 * Hidden when the watchlist is empty or nothing has a sentiment snapshot yet.
 */
export default function BiasHeatmap() {
  const [items, setItems] = useState([]);
  const [scanning, setScanning] = useState(null); // symbol currently being scanned
  const [scanningAll, setScanningAll] = useState(false);
  const navigate = useNavigate();
  const [sort, setSort] = useState(() => {
    try { return localStorage.getItem(SORT_KEY) || "bull_desc"; } catch { return "bull_desc"; }
  });
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) || "compact"; } catch { return "compact"; }
  });

  useEffect(() => {
    let stop = false;
    const load = () => api.get("/watchlist")
      .then((r) => { if (!stop) setItems(r.data.items || []); })
      .catch(() => { if (!stop) setItems([]); });
    load();
    const t = setInterval(load, 60_000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  // Trigger a one-shot sentiment scan for a symbol by hitting the news
  // endpoint with sentiment=true — the backend classifies headlines and
  // inserts a sentiment_history snapshot, which the next /watchlist poll
  // will pick up. We also re-fetch immediately so the empty cell fills in
  // without waiting for the 60s interval.
  const scanSentiment = async (symbol) => {
    setScanning(symbol);
    try {
      await api.get(`/market/news/${symbol}?limit=8&sentiment=true`);
      const r = await api.get("/watchlist");
      setItems(r.data.items || []);
    } catch {
      // Silent — the empty cell is still there, user can retry.
    } finally {
      setScanning(null);
    }
  };

  // Bulk-scan every cell missing a snapshot. Runs sequentially to keep
  // memory + Claude spend predictable — a 20-symbol watchlist will finish
  // in ~30-40s. Refresh happens once at the end.
  const scanAllEmpty = async (empties) => {
    if (empties.length === 0) return;
    setScanningAll(true);
    // Single loading toast that we upcycle per symbol so users see live
    // progress (e.g. "3/5 scanned…") instead of one toast per request.
    const toastId = "heatmap-scan-all";
    toast.loading(`0/${empties.length} scanned…`, { id: toastId });
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < empties.length; i += 1) {
      const sym = empties[i];
      setScanning(sym);
      try {
        await api.get(`/market/news/${sym}?limit=8&sentiment=true`);
        ok += 1;
      } catch {
        // Skip individual failures — a rate-limited symbol shouldn't stop
        // the batch.
        failed += 1;
      }
      toast.loading(
        `${i + 1}/${empties.length} scanned · ${sym}${failed ? ` · ${failed} failed` : ""}`,
        { id: toastId },
      );
    }
    setScanning(null);
    try {
      const r = await api.get("/watchlist");
      setItems(r.data.items || []);
    } catch {}
    if (failed === 0) toast.success(`Scanned ${ok} symbols`, { id: toastId });
    else toast.info(`Scanned ${ok} · ${failed} failed`, { id: toastId });
    setScanningAll(false);
  };

  const changeSort = (v) => {
    setSort(v);
    try { localStorage.setItem(SORT_KEY, v); } catch {}
  };

  const changeDensity = (v) => {
    setDensity(v);
    try { localStorage.setItem(DENSITY_KEY, v); } catch {}
  };

  // Sort a shallow copy — never mutate state. Symbols without a sentiment
  // snapshot always sink to the bottom so the heatmap reads "hottest first".
  const sortedItems = useMemo(() => {
    const arr = [...items];
    const bull = (i) => i.sentiment?.bullish_pct;
    if (sort === "alpha") {
      arr.sort((a, b) => a.symbol.localeCompare(b.symbol));
    } else if (sort === "updated") {
      arr.sort((a, b) => {
        const at = a.sentiment?.at || "";
        const bt = b.sentiment?.at || "";
        // Missing snapshots sink; newest first among those that have one.
        if (!at && !bt) return a.symbol.localeCompare(b.symbol);
        if (!at) return 1;
        if (!bt) return -1;
        return bt.localeCompare(at);
      });
    } else {
      // bull_desc default — missing snapshots sink
      arr.sort((a, b) => {
        const av = bull(a);
        const bv = bull(b);
        if (av == null && bv == null) return a.symbol.localeCompare(b.symbol);
        if (av == null) return 1;
        if (bv == null) return -1;
        return bv - av;
      });
    }
    return arr;
  }, [items, sort]);

  const withData = items.filter((i) => i.sentiment);
  const emptySymbols = items.filter((i) => !i.sentiment).map((i) => i.symbol);
  const showScanAll = emptySymbols.length >= 3;
  if (items.length === 0) return null;

  const cellStyle = (bull) => {
    if (bull == null) return { background: "#27272a", color: "#a1a1aa" };
    // Interpolate red -> zinc -> green centered at 50%. Clamp to keep it readable.
    const t = Math.max(0, Math.min(100, bull)) / 100; // 0..1
    const r = Math.round(255 * (1 - t) * 0.75);
    const g = Math.round(200 * t);
    return { background: `rgba(${r}, ${g}, 50, 0.28)`, color: "#fff" };
  };

  return (
    <div
      className="p-4 border border-zinc-800 rounded-sm bg-zinc-900/40"
      data-testid="bias-heatmap"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Watchlist bias
        </div>
        <div className="flex items-center gap-2">
          <div
            data-testid="heatmap-density-toggle"
            role="tablist"
            aria-label="Heatmap density"
            className="flex items-center border border-zinc-800 rounded-sm overflow-hidden"
          >
            {["compact", "dense"].map((d) => (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={density === d}
                onClick={() => changeDensity(d)}
                data-testid={`heatmap-density-${d}`}
                title={d === "compact" ? "3-column layout with sparklines" : "6-column dense grid — quick scan for large watchlists"}
                className={`mono text-[9px] uppercase tracking-widest px-2 py-1 transition-colors ${
                  density === d
                    ? "bg-zinc-100 text-black"
                    : "text-zinc-500 hover:text-white hover:bg-zinc-800"
                }`}
              >
                {d === "compact" ? "3-col" : "6-col"}
              </button>
            ))}
          </div>
          <div
            data-testid="heatmap-sort-toggle"
            role="tablist"
            aria-label="Sort heatmap"
            className="flex items-center border border-zinc-800 rounded-sm overflow-hidden"
          >
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={sort === o.value}
                onClick={() => changeSort(o.value)}
                data-testid={`heatmap-sort-${o.value}`}
                className={`mono text-[9px] uppercase tracking-widest px-2 py-1 transition-colors ${
                  sort === o.value
                    ? "bg-zinc-100 text-black"
                    : "text-zinc-500 hover:text-white hover:bg-zinc-800"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">
            {withData.length}/{items.length} tracked
          </div>
          {showScanAll && (
            <button
              type="button"
              data-testid="heatmap-scan-all-btn"
              disabled={scanningAll}
              onClick={() => scanAllEmpty(emptySymbols)}
              title={`Scrape headlines + tag sentiment for ${emptySymbols.length} untracked symbols`}
              className="mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-[#00C805]/50 text-[#00C805] hover:bg-[#00C805]/10 disabled:opacity-50 disabled:cursor-wait transition-colors"
            >
              {scanningAll ? `Scanning ${emptySymbols.length}…` : `+ Scan all (${emptySymbols.length})`}
            </button>
          )}
        </div>
      </div>
      <div
        data-testid={`heatmap-grid-${density}`}
        className={density === "dense" ? "grid grid-cols-6 gap-1" : "grid grid-cols-3 gap-1.5"}
      >
        {sortedItems.map((i) => {
          const spark = i.sentiment?.sparkline || [];
          const delta = spark.length >= 2 ? spark[spark.length - 1] - spark[0] : null;
          return (
            <Link
              key={i.symbol}
              to={`/trade?s=${i.symbol}`}
              data-testid={`heatmap-cell-${i.symbol}`}
              style={cellStyle(i.sentiment?.bullish_pct)}
              title={i.sentiment
                ? `${i.symbol} — ${Math.round(i.sentiment.bullish_pct)}% bull / ${Math.round(i.sentiment.bearish_pct)}% bear${
                    delta !== null ? ` · Δ${delta > 0 ? "+" : ""}${delta.toFixed(0)}% over last ${spark.length}` : ""
                  }`
                : `${i.symbol} — no bias snapshot yet`}
              className={`rounded-sm hover:ring-1 hover:ring-white/40 transition-shadow ${
                density === "dense" ? "px-1.5 py-1" : "px-2 py-1.5"
              }`}
            >
              <div className="flex items-baseline justify-between gap-1">
                <div className={`mono font-bold ${density === "dense" ? "text-[10px]" : "text-xs"}`}>{i.symbol}</div>
                {density !== "dense" && delta !== null && (
                  <div
                    data-testid={`heatmap-delta-${i.symbol}`}
                    className="mono text-[8px] font-bold leading-none"
                    style={{ color: delta > 0 ? "#00C805" : delta < 0 ? "#FF3B30" : "rgba(255,255,255,0.5)" }}
                  >
                    {delta > 0 ? "▲" : delta < 0 ? "▼" : "·"}
                    {Math.abs(Math.round(delta))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-1 mt-0.5">
                <div className={`mono opacity-80 ${density === "dense" ? "text-[8px]" : "text-[9px]"}`}>
                  {i.sentiment ? `${Math.round(i.sentiment.bullish_pct)}%` : (
                    <button
                      type="button"
                      data-testid={`heatmap-scan-btn-${i.symbol}`}
                      disabled={scanning === i.symbol}
                      title={`Scrape recent ${i.symbol} headlines and tag them with Claude to seed a sentiment snapshot`}
                      onClick={(e) => {
                        // Don't follow the outer Link to /trade — this is a
                        // data-fetch action, not navigation.
                        e.preventDefault();
                        e.stopPropagation();
                        scanSentiment(i.symbol);
                      }}
                      className={`mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-white/20 hover:border-white/60 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-wait ${
                        density === "dense" ? "text-[7px]" : "text-[8px]"
                      }`}
                    >
                      {scanning === i.symbol ? "Scanning…" : "+ Scan"}
                    </button>
                  )}
                </div>
                {density !== "dense" && spark.length >= 2 && (
                  <button
                    type="button"
                    data-testid={`heatmap-spark-btn-${i.symbol}`}
                    title={`View ${i.symbol} sentiment trend & headlines`}
                    onClick={(e) => {
                      // Stop the outer <Link> from navigating to /trade
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/positions?s=${i.symbol}&drawer=news`);
                    }}
                    className="p-0.5 -m-0.5 rounded-sm hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <MoodSpark
                      data={spark}
                      delta={delta}
                      testId={`heatmap-spark-${i.symbol}`}
                    />
                  </button>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest mt-2 flex items-center justify-between">
        <span className="text-[#FF3B30]">bear</span>
        <span>bull %</span>
        <span className="text-[#00C805]">bull</span>
      </div>
    </div>
  );
}
